/**
 * AA login "Admin Control Screens" hub — single place for product/config controls.
 * Individual pages keep their own routes; this list is the launcher UI.
 *
 * Also drives operator Edit Permissions (via getAdminControlPermissionRoutes).
 * When you add a card here, it becomes an on/off grant option automatically
 * (path → permission key). Keep backend/config/adminControlScreens.js in sync
 * for API-driven clients.
 */

export const ADMIN_CONTROL_HUB_ROLES = ['app_admin', 'super_admin', 'app_operator'];

/** @typedef {{ title: string, description: string, path: string, roles: string[], permission?: string, keywords?: string[], icon?: string, tint?: string }} AdminControlItem */
/** @typedef {{ id: string, title: string, subtitle: string, items: AdminControlItem[] }} AdminControlSection */

/** Path → permission_name (same rules as backend routeRegistry). */
export function generateAdminControlPermissionName(path) {
  return String(path || '')
    .replace(/^\//, '')
    .replace(/\//g, '_')
    .replace(/-/g, '_')
    .toLowerCase();
}

export function getAdminControlItemPermission(item) {
  if (!item) return '';
  if (item.permission) return String(item.permission).toLowerCase();
  return generateAdminControlPermissionName(item.path);
}

/** @type {AdminControlSection[]} */
export const ADMIN_CONTROL_SECTIONS = [
  {
    id: 'apptrends',
    title: 'App Trends',
    subtitle: 'Loksy & Vyapar installations, registrations, day and month stats',
    items: [
      {
        title: 'App Trends',
        description:
          'Day-wise and month-wise Loksy & Vyapar installations, registered users, and activity',
        path: '/apptrends',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'apptrends',
          'app trends',
          'installations',
          'installs',
          'registered users',
          'day wise',
          'month wise',
          'statistics',
          'stats',
          'loksy',
          'vyapar',
          'citizen',
          'mitra',
        ],
        icon: 'Insights',
        tint: '#6d28d9',
      },
    ],
  },
  {
    id: 'screens',
    title: 'Screens Controls',
    subtitle: 'Mitra, Citizen, MyTown, cards & town merchandising',
    items: [
      {
        title: 'Mitra Screens',
        description: 'Locsy Mitra hub cards, badges, thumbnails & order',
        path: '/mitrascreens',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['mitra', 'screens', 'mitrascreens'],
        icon: 'Storefront',
        tint: '#0284c7',
      },
      {
        title: 'Loksy Home Screen Controls',
        description:
          'Hide / display Citizen home categories — Govt Alerts, Real Estate, Food Zone, Movies & Events…',
        path: '/loksyhomescreen_controls',
        // Keep grant key stable with legacy /citizenscreens operators.
        permission: 'citizenscreens',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'loksyhomescreen_controls',
          'loksy home screen controls',
          'citizen screens',
          'citizenscreens',
          'citizen home',
          'home categories',
          'govt alerts',
          'real estate',
          'movies events',
          'food zone',
          'hide section',
          'display section',
          'home carousel',
        ],
        icon: 'PhoneAndroid',
        tint: '#7c3aed',
      },
      {
        title: 'MyTown Screens',
        description: 'Citizen MyTown category cards — images, labels, hide & order',
        path: '/mytownscreens',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'mytown',
          'my town',
          'mytownscreens',
          'categories',
          'kirana',
          'category cards',
          'thumbnails',
        ],
        icon: 'GridView',
        tint: '#ea580c',
      },
      {
        title: 'Dashboard Cards Control',
        description: 'Show / hide / reorder user dashboard cards',
        path: '/dashboard-cards',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['dashboard cards', 'visibility'],
        icon: 'DashboardCustomize',
        tint: '#0f766e',
      },
      {
        title: 'Town Pulse Screen',
        description: 'My Town updates — live / demo ads & status',
        path: '/content/townpulsescreen',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['town pulse', 'townpulsescreen'],
        icon: 'Newspaper',
        tint: '#c2410c',
      },
    ],
  },
  {
    id: 'engagement',
    title: 'Wheel & Growth Controls',
    subtitle: 'Spin, boost, coupons & ads pricing',
    items: [
      {
        title: 'Shop polls',
        description: 'Mitra customer polls — live votes, extra options & shop stats',
        path: '/content/shop-polls',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'shop polls',
          'polls',
          'poll statistics',
          'customer poll',
          'vote',
          'curry poll',
          'today poll',
        ],
        icon: 'HowToVote',
        tint: '#0369a1',
      },
      {
        title: 'Locsy Spin (Wheel)',
        description: 'Lucky wheel probabilities, coupons & shop spin',
        path: '/content/lucky-wheel',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['wheel', 'lucky wheel', 'locsy spin'],
        icon: 'Casino',
        tint: '#db2777',
      },
      {
        title: 'Boost Control',
        description: 'Coins, points & referral reward rules',
        path: '/content/boost-control',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['boost', 'coins', 'points'],
        icon: 'RocketLaunch',
        tint: '#ea580c',
      },
      {
        title: 'Coin Management',
        description: 'Loksy Wallet recharge, earn/spend rules, ledger & legal',
        path: '/content/coin-management',
        roles: [],
        keywords: [
          'coin management',
          'coins',
          'wallet',
          'wallet management',
          'wallet balance',
          'add points',
          'add coins',
          'recharge plans',
          'earn rules',
          'spend rules',
          'coin ledger',
          'coin analytics',
          'coin terms',
          'refund policy',
        ],
        icon: 'AccountBalanceWallet',
        tint: '#0f766e',
      },
      {
        title: 'Razorpay',
        description:
          'All gateway receipts, refunds, bank settlements (Mitra, Citizen, ads, wallet, LMS)',
        path: '/content/razorpay',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'razorpay',
          'razor pay',
          'razor-pay',
          'payments',
          'receipts',
          'settlement',
          'settled',
          'bank',
          'utr',
          'inflow',
          'outflow',
          'refunds',
        ],
        icon: 'AccountBalance',
        tint: '#0f766e',
      },
      {
        title: 'Coupon Flow',
        description: 'DigiDrop / ad coupon share rules',
        path: '/content/coupon-flow',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['coupon flow', 'digidrop'],
        icon: 'LocalOffer',
        tint: '#ca8a04',
      },
      {
        title: 'Citizen Coupon Management',
        description: 'Ad types, costs, worth bands, caps & spinner ON/OFF',
        path: '/content/coupon-management',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'citizen coupon management',
          'coupon management',
          'ad types',
          'worth',
          'digidrop',
          'spinner',
        ],
        icon: 'CardGiftcard',
        tint: '#7c3aed',
      },
      {
        title: 'Manage Ads',
        description: 'Advertisement daily rates, limits & payment timing',
        path: '/content/ad-settings',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['ads', 'advertisement settings'],
        icon: 'Campaign',
        tint: '#dc2626',
      },
    ],
  },
  {
    id: 'approvals',
    title: 'Admin Approvals',
    subtitle: 'Verification & moderation',
    items: [
      {
        title: 'vyapar registration form',
        description:
          'Assisted Mitra / Vyapar registration when OTP is unavailable',
        path: '/business-admin/register',
        permission: 'vyapar_assisted_registration',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        icon: 'VerifiedUser',
        keywords: [
          'vyapar',
          'registration',
          'otp',
          'assisted',
          'mitra',
          'vyapar registration form',
          'mitra register form',
          'mitra registration',
          'mitra registration by admin',
          'register without otp',
          'admin assisted mitra',
          'vyapar registration',
          'register vyapar',
          'business-admin/register',
        ],
      },
      {
        title: 'Business Admin Verification',
        description: 'Approve / reject business admin registrations',
        path: '/business-admin/verification',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        permission: 'business_admin_approval',
        keywords: [
          'approvals',
          'verification',
          'business admin',
          'manage loksy mitras',
          'manage locsy mitras',
          'manage mitras',
          'loksy mitras',
          'locsy mitras',
        ],
        icon: 'HowToReg',
        tint: '#16a34a',
      },
      {
        title: 'Citizen Listings',
        description: 'Approve, pause or resume citizen marketplace posts',
        path: '/user-postings',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['citizen listings', 'user postings', 'approvals'],
        icon: 'PostAdd',
        tint: '#2563eb',
      },
      {
        title: 'Posts By Businesses',
        description: 'Moderate business posts / digital pamphlets',
        path: '/content/business-posts',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['business posts', 'approvals'],
        icon: 'Article',
        tint: '#4f46e5',
      },
      {
        title: 'Job Alerts Approval',
        description: 'Review job alert submissions',
        path: '/job-alerts/approval',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['job alerts', 'approvals'],
        icon: 'WorkOutline',
        tint: '#0891b2',
      },
      {
        title: 'Learn Zone Approvals',
        description:
          'Approve Local Tutor / Mitra courses for Online & Campus Classes (KNLS stays separate)',
        path: '/content/learn-zone-approvals',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'learn zone',
          'local tutors',
          'online courses',
          'campus classes',
          'lms',
          'approvals',
        ],
        icon: 'School',
        tint: '#9333ea',
      },
      {
        title: 'Online Course Sales',
        description:
          'KNLS online purchase totals + Mitra/third-party rows (excluded from our sales)',
        path: '/content/online-course-sales',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'online sales',
          'online course sales',
          'learn zone sales',
          'knls sales',
          'course purchases',
        ],
        icon: 'PointOfSale',
        tint: '#0f766e',
      },
      {
        title: 'Loksy Navigators Approval',
        description:
          'Approve / reject / suspend Navigators, assign town & district (field force)',
        path: '/content/navigators',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'approvals',
          'navigator',
          'navigators',
          'navigator approval',
          'field force',
          'pending navigator',
          'loksy navigator',
        ],
        icon: 'HowToReg',
        tint: '#ea580c',
      },
    ],
  },
  {
    id: 'catalog',
    title: 'Catalog & Categories',
    subtitle: 'Indexes, templates & posting categories',
    items: [
      {
        title: 'Shops & Services Index',
        description: 'Business registration categories',
        path: '/business-category-index',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['shops services index', 'categories'],
        icon: 'Category',
        tint: '#0d9488',
      },
      {
        title: 'Mitra Token Board Settings',
        description:
          'Category-wise Token Board defaults — prepaid/postpaid, packs, tables, theme',
        path: '/mitra-token-board-settings',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'token board',
          'token board settings',
          'prepaid',
          'postpaid',
          'pay when leaving',
          'restaurant',
          'mitra token',
          'category defaults',
        ],
        icon: 'ConfirmationNumber',
        tint: '#c2410c',
      },
      {
        title: 'Citizen Posting Categories',
        description: 'OLX / requirements / to-let categories',
        path: '/citizen-posting-categories',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['citizen posting categories'],
        icon: 'ListAlt',
        tint: '#64748b',
      },
      {
        title: 'Catalog Templates',
        description: 'Smart Bill default product lists by shop type',
        path: '/catalog-templates',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['catalog', 'templates', 'kirana'],
        icon: 'Inventory2',
        tint: '#b45309',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Platform Settings',
    subtitle: 'Career, billing UPI, WhatsApp & system',
    items: [
      {
        title: 'Resume Controls',
        description: 'PDF / Word / Edit prices, UPI & career phone lock',
        path: '/content/career-resume-settings',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'career',
          'resume',
          'resume controls',
          'phone editable',
          'pdf fee',
          'docx fee',
          'edit fee',
        ],
        icon: 'Description',
        tint: '#1d4ed8',
      },
      {
        title: 'Billing UPI Controls',
        description: 'Default UPI ID for billing QR / payments',
        path: '/bills/admin-controls',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['upi', 'admin controls', 'billing'],
        icon: 'QrCode2',
        tint: '#059669',
      },
      {
        title: 'WhatsApp Message Config',
        description: 'WhatsApp bill / notification message templates',
        path: '/bills/whatsapp-config',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['whatsapp', 'message config'],
        icon: 'Chat',
        tint: '#25d366',
      },
      {
        title: 'System Settings',
        description: 'App name, maintenance, registration & notifications',
        path: '/settings',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['settings', 'maintenance'],
        icon: 'Settings',
        tint: '#475569',
      },
      {
        title: 'BetaGate',
        description:
          'Early access ON/OFF, launch date, phone allowlist & Mitra bill citizen login',
        path: '/betagate',
        roles: ['app_admin', 'super_admin'],
        keywords: [
          'betagate',
          'beta gate',
          'early access',
          'prelaunch',
          'countdown',
          'allowlist',
          'launch',
          'bill users',
        ],
        icon: 'LockOpen',
        tint: '#d97706',
      },
      {
        title: 'App Notifications',
        description: 'Push notification campaigns & alerts',
        path: '/appnotifications',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['notifications', 'push'],
        icon: 'NotificationsActive',
        tint: '#e11d48',
      },
      {
        title: 'Franchisee SaaS Pricing',
        description: 'Franchisee registration / SaaS fee pricing',
        path: '/locsy/admin/franchisee-saas-pricing',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: ['saas', 'franchisee pricing'],
        icon: 'PriceChange',
        tint: '#a16207',
      },
      {
        title: 'Membership Plans',
        description:
          'Free / Basic / Advance / Prime — prices, trial, volume caps, add-ons',
        path: '/content/membership-plans',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'membership',
          'membership plans',
          'loksy',
          'connect',
          'elite',
          'prime',
          'saas tiers',
          'pricing',
        ],
        icon: 'WorkspacePremium',
        tint: '#c2410c',
      },
      {
        title: 'Nav App Controls',
        description:
          'Approval mandatory toggle, commissions ON/OFF, rates & UPI payout settings',
        path: '/content/nav-app-controls',
        roles: ['app_admin', 'super_admin', 'app_operator'],
        keywords: [
          'nav app',
          'navigator controls',
          'commission rates',
          'approval mandatory',
          'navigator settings',
        ],
        icon: 'Tune',
        tint: '#c2410c',
      },
    ],
  },
];

/** Paths that live in the hub — hide from AA sidebar when user can open the hub. */
export function getAdminControlHubPaths() {
  const paths = new Set();
  for (const section of ADMIN_CONTROL_SECTIONS) {
    for (const item of section.items) {
      paths.add(item.path);
    }
  }
  // Legacy alias kept as a live route for bookmarks / older deep links.
  paths.add('/citizenscreens');
  return paths;
}

/** Flat routes for Edit Permissions UI (merged with API response). */
export function getAdminControlPermissionRoutes() {
  const routes = [];
  for (const section of ADMIN_CONTROL_SECTIONS) {
    const category = `Admin Control Screens — ${section.title}`;
    for (const item of section.items) {
      routes.push({
        path: item.path,
        name: item.title,
        description: item.description,
        permission: getAdminControlItemPermission(item),
        category,
        operatorAccessible: true,
      });
    }
  }
  return routes;
}

export function getAdminControlPermissionNames() {
  return getAdminControlPermissionRoutes().map((r) => r.permission);
}

export function operatorHasAnyAdminControlPermission(permissions) {
  const set = new Set(
    (Array.isArray(permissions) ? permissions : []).map((p) =>
      String(p).toLowerCase(),
    ),
  );
  if (set.has('*')) return true;
  return getAdminControlPermissionNames().some((p) => set.has(p));
}

/**
 * Hub card visibility.
 * app_admin / super_admin: all cards for their role.
 * app_operator: must have the card's permission granted (default off).
 */
export function userCanAccessAdminControlItem(user, item) {
  if (!user?.role || !item?.roles?.includes(user.role)) return false;
  if (user.role !== 'app_operator') return true;

  const perms = (user.permissions || []).map((p) => String(p).toLowerCase());
  if (perms.includes('*')) return true;
  const needed = getAdminControlItemPermission(item);
  return Boolean(needed) && perms.includes(needed);
}
